import React from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { KeyRound, AlertCircle } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { cn } from '@/lib/utils'
import { useTheme } from '@/lib/ThemeContext'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { MIN_PASSWORD_LENGTH, passwordFailureKey } from '@/lib/passwordRule'

interface ChangePasswordDialogProps {
  open: boolean
  onSuccess: () => void
}

export const ChangePasswordDialog = ({ open, onSuccess }: ChangePasswordDialogProps) => {
  const { t } = useTranslation('auth')
  const { theme } = useTheme()
  const { changePassword } = useAuth()
  const [currentPassword, setCurrentPassword] = React.useState('')
  const [newPassword, setNewPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  // A broken rule shows under the new-password field; anything else below the form.
  const [newPasswordError, setNewPasswordError] = React.useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setNewPasswordError('')

    const failure = passwordFailureKey(newPassword)
    if (failure) {
      setNewPasswordError(t(failure))
      return
    }

    if (newPassword !== confirmPassword) {
      setError(t('passwordsMustMatch'))
      return
    }

    setIsLoading(true)

    try {
      await changePassword(currentPassword, newPassword)

      onSuccess()
    } catch (err) {
      const { message, status, param } = apiErrorMessage(err)
      if (status === 400 && param === 'newPassword') setNewPasswordError(message || t(passwordFailureKey(newPassword) || 'passwordMinLength'))
      else setError(message || t('changePasswordError'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[440px] p-0 overflow-hidden bg-white/80 backdrop-blur-2xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.04)] rounded-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="sr-only">{t('changePasswordTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="p-8 sm:p-10 relative">
            <div
              className="absolute inset-0 bg-gradient-to-b via-white/20 to-transparent pointer-events-none"
              style={{
                backgroundImage: `linear-gradient(to bottom, ${theme.primary[500]}0A, rgba(255,255,255,0.2), transparent)`
              }}
            />

            <div className="flex items-center justify-center mb-6">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${theme.primary[100]}` }}
              >
                <AlertCircle className="w-8 h-8" style={{ color: theme.primary[500] }} />
              </div>
            </div>

            <h2 className="text-2xl font-semibold mb-2 text-gray-800 text-center">
              {t('changePasswordTitle')}
            </h2>
            <p className="text-gray-600 text-base mb-8 text-center">
              {t('changePasswordDescription')}
            </p>

            <div className="space-y-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder={t('currentPasswordPlaceholder')}
                  className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 text-gray-800 text-base transition-all duration-200 rounded-xl"
                  style={{
                    borderColor: 'rgba(255,255,255,0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500]
                    e.target.style.boxShadow = `0 0 0 3px ${theme.primary[100]}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.2)'
                    e.target.style.boxShadow = 'none'
                  }}
                  required
                />
              </div>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('newPasswordPlaceholder')}
                  className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 text-gray-800 text-base transition-all duration-200 rounded-xl"
                  style={{
                    borderColor: 'rgba(255,255,255,0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500]
                    e.target.style.boxShadow = `0 0 0 3px ${theme.primary[100]}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.2)'
                    e.target.style.boxShadow = 'none'
                  }}
                  required
                  aria-invalid={Boolean(newPasswordError)}
                  aria-describedby="new-password-rule"
                />
              </div>
              <p
                id="new-password-rule"
                className={cn('-mt-2 text-sm', newPasswordError ? 'text-red-600' : 'text-gray-500')}
              >
                {newPasswordError || t('setPassword.complexRule', { count: MIN_PASSWORD_LENGTH })}
              </p>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <KeyRound className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('confirmPasswordPlaceholder')}
                  className="pl-10 h-12 bg-white/90 backdrop-blur-sm border-white/20 hover:border-white/30 text-gray-800 text-base transition-all duration-200 rounded-xl"
                  style={{
                    borderColor: 'rgba(255,255,255,0.2)',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = theme.primary[500]
                    e.target.style.boxShadow = `0 0 0 3px ${theme.primary[100]}`
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.2)'
                    e.target.style.boxShadow = 'none'
                  }}
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 text-base rounded-xl text-white transition-all duration-200"
                style={{
                  backgroundColor: theme.primary[500],
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = theme.primary[600]
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = theme.primary[500]
                }}
              >
                {isLoading ? t('changingPassword') : t('changePasswordButton')}
              </Button>
            </div>

            {error && (
              <div className="mt-4 backdrop-blur-sm border rounded-xl p-4 bg-red-50/60 border-red-100/50">
                <p className="text-base text-center font-light text-red-500">{error}</p>
              </div>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
